import { ReactElement } from 'react';
import { useToggle } from 'react-use';

import { MedicineSimple } from '@dreryk/drerykweb-medicine-api';
import { PatientReimbursement } from '@dreryk/egabinet-prescription-frontend-api';
import { VisitDiagnosis } from '@dreryk/egabinet-visit-frontend-api';

import { ModalFooterItemConfiguration } from '@dreryk/react-components';

import { OnReimbursementClick } from '../Reimbursement/Reimbursement.types';

import { MedicineDetailsModal } from './MedicineDetailsModal';

type MedicineDetailsModalPropsWithReimbursements = {
   reimbursements: PatientReimbursement[];
   visitDiagnoses: VisitDiagnosis[];
};

type MedicineDetailsModalPropsWithoutReimbursements = {
   reimbursements?: never;
   visitDiagnoses?: never;
};

type MedicineDetailsModalOpenActionProps = {
   readonly render: (openModal: () => void) => ReactElement;
   readonly medicine: Omit<MedicineSimple, 'narcoticOrPsychotropic' | 'inPersonExaminationRequired'>;
   readonly onReimbursementClick?: OnReimbursementClick;
   readonly confirmationButtonLabel?: string;
   readonly onConfirm?: VoidFunction | null;
} & (MedicineDetailsModalPropsWithReimbursements | MedicineDetailsModalPropsWithoutReimbursements);

const MedicineDetailsModalOpenAction = (props: MedicineDetailsModalOpenActionProps): JSX.Element => {
   const [showModal, toggleShowModal] = useToggle(false);
   const handleConfirm = (): void => {
      if (props.onConfirm) {
         props.onConfirm();
      }
      toggleShowModal(false);
   };

   const footerItems: ModalFooterItemConfiguration[] =
      props.onConfirm && props.confirmationButtonLabel
         ? [
              {
                 onClick: handleConfirm,
                 label: props.confirmationButtonLabel,
                 key: 'confirm',
                 testId: 'openMedicineDetails',
              },
           ]
         : [];

   return (
      <>
         {props.render(toggleShowModal)}
         {showModal ? (
            <>
               {props.reimbursements && props.visitDiagnoses ? (
                  <MedicineDetailsModal
                     footerItems={footerItems}
                     medicine={props.medicine}
                     reimbursements={props.reimbursements}
                     visitDiagnoses={props.visitDiagnoses}
                     onCancel={toggleShowModal}
                     onReimbursementClick={
                        props.onReimbursementClick
                           ? (reimbursement) => {
                                props.onReimbursementClick!(reimbursement);
                                toggleShowModal();
                             }
                           : undefined
                     }
                  />
               ) : (
                  <MedicineDetailsModal
                     footerItems={footerItems}
                     medicine={props.medicine}
                     onCancel={toggleShowModal}
                     onReimbursementClick={
                        props.onReimbursementClick
                           ? (reimbursement) => {
                                props.onReimbursementClick!(reimbursement);
                                toggleShowModal();
                             }
                           : undefined
                     }
                  />
               )}
            </>
         ) : null}
      </>
   );
};

export { MedicineDetailsModalOpenAction };
